<script>
import ProjectMomService from "@/services/ProjectMomService";
import MomParticipantService from "@/services/MomParticipantService";
import VProjectMomForm from "@/components/Forms/VProjectMomForm.vue";
import VProjectMomNextMeetingForm from "@/components/Forms/VProjectMomNextMeetingForm.vue";
import VProjectMomParticipantsForm from "@/components/Forms/VProjectMomParticipantsForm.vue";
import VProjectActions from "./components/VProjectActions.vue";
import VActionableActions from "./components/VActionableActions.vue";
import VControl from "@/components/Planning/Helpers/VControl";
import { EventBus } from "@/libraries/EventBus";
import dayjs from "dayjs";

export default {
    props: {
        project_id: {},
        mom_id: {}
    },
    components: {
        VProjectMomForm,
        VProjectMomNextMeetingForm,
        VProjectMomParticipantsForm,
        VProjectActions,
        VActionableActions,
        VControl
    },
    data() {
        return {
            loading: true,
            mom: null,
            participants: [],
            showEditMomModal: false,
            showNextMeetingModal: false,
            showParticipantsModal: false,
            showHeader: true,
            showNextMeeting: true
        };
    },
    computed: {
        presentParticipants() {
            return this.participants.filter(p => p.present == 1);
        },
        absentParticipants() {
            return this.participants.filter(p => p.present != 1);
        }
    },
    async created() {
        EventBus.$on("data-tab-open", () => {
            this.showHeader = true;
            this.showNextMeeting = true;
        });
        EventBus.$on("data-tab-close", () => {
            this.showHeader = false;
            this.showNextMeeting = false;
        });

        await Promise.all([this.fetchMom(), this.fetchParticipants()]);
        this.loading = false;
    },
    beforeDestroy() {
        EventBus.$off("data-tab-open");
        EventBus.$off("data-tab-close");
    },
    methods: {
        eventize(type) {
            EventBus.$emit(
                type === "open" ? "data-tab-open" : "data-tab-close"
            );
        },
        async fetchMom() {
            try {
                const { data } = await ProjectMomService.GETS(this.mom_id);
                this.mom = data.data;
            } catch (e) {
                console.log("fetchMom e", e);
            }
        },
        async fetchParticipants() {
            try {
                const { data } = await MomParticipantService.GET({
                    project_mom_id: this.mom_id
                });
                this.participants = data.data;
            } catch (e) {
                console.log("fetchParticipants e", e);
            }
        },
        participantName(p) {
            if (p.user_id && p.user) return p.user.name;
            return p.name || "---";
        },
        async onMomUpdated() {
            this.showEditMomModal = false;
            await this.fetchMom();
        },
        async onNextMeetingUpdated() {
            this.showNextMeetingModal = false;
            await this.fetchMom();
        },
        async onParticipantsClosed() {
            this.showParticipantsModal = false;
            await this.fetchParticipants();
        },
        downloadPdf() {
            fetch(`/v1/project-moms/pdf/${this.mom_id}`)
                .then(response => {
                    if (!response.ok) throw new Error("Failed to fetch PDF");
                    return response.blob();
                })
                .then(blob => {
                    const link = document.createElement("a");
                    link.href = URL.createObjectURL(blob);
                    link.download = `mom-${this.mom_id}-${dayjs().format(
                        "DD-MM-YYYY"
                    )}.pdf`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    URL.revokeObjectURL(link.href);
                })
                .catch(e => console.error(e));
        }
    }
};
</script>

<template>
    <div class="v-project-mom-detail">
        <base-header class="pb-1" type="">
            <bread-bag>
                <base-button
                    type="secondary"
                    size="sm"
                    @click="
                        $router.push({
                            name: 'ProjectPlanningMomsTab',
                            params: { project_id: project_id }
                        })
                    "
                >
                    <i class="far fa-arrow-left"></i>
                    {{ $t("moms.back_to_list") }}
                </base-button>
                <base-button @click="eventize('open')" type="primary" size="sm">
                    <i class="far fa-plus"></i>
                    {{ $t("milestones.expand_all") }}
                </base-button>
                <base-button
                    @click="eventize('close')"
                    type="primary"
                    size="sm"
                >
                    <i class="far fa-minus"></i>
                    {{ $t("milestones.fold_all") }}
                </base-button>
            </bread-bag>
        </base-header>

        <v-loader :loading="loading" v-if="loading" />
        <template v-else-if="mom">
            <!-- MoM header block -->
            <div class="container-fluid">
                <div class="row card-wrapper">
                    <div class="col-12">
                        <div class="mb-3 d-flex align-items-center">
                            <v-control
                                :show="showHeader"
                                @trigger="showHeader = !showHeader"
                            >
                                <h3 class="pb-0 mb-0">
                                    {{ mom.title }}
                                    <span
                                        class="text-muted font-weight-light ml-2"
                                        v-if="mom.held_date"
                                    >
                                        {{
                                            $d(new Date(mom.held_date), "short")
                                        }}
                                    </span>
                                </h3>
                            </v-control>
                            <i
                                class="far fa-pencil fa-fw m-click text-muted ml-3"
                                @click="showEditMomModal = true"
                            ></i>
                            <base-button
                                class="ml-auto"
                                type="primary"
                                :outline="true"
                                size="sm"
                                @click="downloadPdf"
                            >
                                <i class="far fa-file-pdf"></i>
                                PDF
                            </base-button>
                        </div>

                        <template v-if="showHeader">
                            <card body-classes="px-0">
                                <v-table class="table table-mg table-sm">
                                    <v-thead>
                                        <v-tr>
                                            <v-th>
                                                {{
                                                    $t(
                                                        "moms.participants_present"
                                                    )
                                                }}
                                                <i
                                                    class="far fa-pencil fa-fw m-click text-muted ml-1"
                                                    @click="
                                                        showParticipantsModal = true
                                                    "
                                                ></i>
                                            </v-th>
                                            <v-th>
                                                {{
                                                    $t(
                                                        "moms.participants_absent"
                                                    )
                                                }}
                                                <i
                                                    class="far fa-pencil fa-fw m-click text-muted ml-1"
                                                    @click="
                                                        showParticipantsModal = true
                                                    "
                                                ></i>
                                            </v-th>
                                            <v-th>
                                                {{ $t("moms.field_location") }}
                                                <i
                                                    class="far fa-pencil fa-fw m-click text-muted ml-1"
                                                    @click="
                                                        showEditMomModal = true
                                                    "
                                                ></i>
                                            </v-th>
                                        </v-tr>
                                    </v-thead>
                                    <v-tbody>
                                        <v-tr>
                                            <v-td class="align-top">
                                                <div
                                                    v-for="p in presentParticipants"
                                                    :key="p.id"
                                                    :title="
                                                        p.email ||
                                                            (p.user &&
                                                                p.user.email) ||
                                                            ''
                                                    "
                                                >
                                                    {{ participantName(p) }}
                                                    <i
                                                        v-if="!p.user_id"
                                                        class="far fa-external-link-alt fa-fw text-muted ml-1"
                                                    ></i>
                                                </div>
                                                <span
                                                    v-if="
                                                        !presentParticipants.length
                                                    "
                                                    class="text-muted"
                                                    >---</span
                                                >
                                            </v-td>
                                            <v-td class="align-top">
                                                <div
                                                    v-for="p in absentParticipants"
                                                    :key="p.id"
                                                    :title="
                                                        p.email ||
                                                            (p.user &&
                                                                p.user.email) ||
                                                            ''
                                                    "
                                                >
                                                    {{ participantName(p) }}
                                                    <i
                                                        v-if="!p.user_id"
                                                        class="far fa-external-link-alt fa-fw text-muted ml-1"
                                                    ></i>
                                                </div>
                                                <span
                                                    v-if="
                                                        !absentParticipants.length
                                                    "
                                                    class="text-muted"
                                                    >---</span
                                                >
                                            </v-td>
                                            <v-td
                                                class="align-top preserve-newlines"
                                            >
                                                {{ mom.location || "---" }}
                                            </v-td>
                                        </v-tr>
                                    </v-tbody>
                                </v-table>
                            </card>
                        </template>
                    </div>
                </div>
            </div>

            <!-- Tasks -->
            <v-actionable-actions
                :project_id="project_id"
                :project_mom_id="mom_id"
            />

            <!-- Statuses -->
            <v-project-actions
                :project_id="project_id"
                :project_mom_id="mom_id"
            />

            <!-- Next meeting -->
            <div class="container-fluid">
                <div class="row card-wrapper">
                    <div class="col-12">
                        <div class="mb-3 d-flex align-items-center">
                            <v-control
                                :show="showNextMeeting"
                                @trigger="showNextMeeting = !showNextMeeting"
                            >
                                <h3 class="pb-0 mb-0">
                                    {{ $t("moms.next_meeting_section") }}
                                </h3>
                            </v-control>
                            <i
                                class="far fa-pencil fa-fw m-click text-muted ml-3"
                                @click="showNextMeetingModal = true"
                            ></i>
                        </div>

                        <template v-if="showNextMeeting">
                            <card body-classes="px-0">
                                <v-table class="table table-mg table-sm">
                                    <v-thead>
                                        <v-tr>
                                            <v-th>{{
                                                $t("moms.next_meeting_col_date")
                                            }}</v-th>
                                            <v-th>{{
                                                $t(
                                                    "moms.next_meeting_col_location"
                                                )
                                            }}</v-th>
                                        </v-tr>
                                    </v-thead>
                                    <v-tbody>
                                        <v-tr>
                                            <v-td class="preserve-newlines">
                                                <template v-if="mom.next_date">
                                                    {{
                                                        $d(
                                                            new Date(
                                                                mom.next_date
                                                            ),
                                                            "long"
                                                        )
                                                    }}
                                                </template>
                                                <template v-else>---</template>
                                            </v-td>
                                            <v-td class="preserve-newlines">
                                                {{ mom.next_location || "---" }}
                                            </v-td>
                                        </v-tr>
                                    </v-tbody>
                                </v-table>
                            </card>
                        </template>
                    </div>
                </div>
            </div>
        </template>

        <portal to="modals">
            <v-project-mom-form
                v-if="showEditMomModal"
                :project-id="project_id"
                :mom-id="mom_id"
                @close="showEditMomModal = false"
                @close-reload="onMomUpdated"
            />
            <v-project-mom-next-meeting-form
                v-if="showNextMeetingModal && mom"
                :mom="mom"
                @close="showNextMeetingModal = false"
                @close-reload="onNextMeetingUpdated"
            />
            <v-project-mom-participants-form
                v-if="showParticipantsModal"
                :mom-id="mom_id"
                :project-id="project_id"
                @close="onParticipantsClosed"
            />
        </portal>
    </div>
</template>

<style scoped>
.preserve-newlines {
    white-space: pre-line;
}
</style>
